import { Capacitor } from '@capacitor/core';
import { franchiseApi } from './franchiseApi';
import type { PushPlatform } from '../types/notifications';

// Where usePushNotifications keeps this device's current FCM token. Read here as well because sign-out
// happens in AuthProvider, which sits above the push provider and can't reach its state.
export const PUSH_TOKEN_STORAGE_KEY = 'push_token';
export const PUSH_TOKEN_UPDATED_STORAGE_KEY = 'push_token_updated';

// Set when the user turns push off on this device. Launch-time auto-registration would otherwise
// mint a fresh token on the next start, since the OS permission is still granted, and quietly undo it.
const PUSH_OPTED_OUT_STORAGE_KEY = 'push_opted_out';

export function isPushOptedOut(): boolean {
  return localStorage.getItem(PUSH_OPTED_OUT_STORAGE_KEY) === 'true';
}

export function setPushOptedOut(optedOut: boolean): void {
  if (optedOut) localStorage.setItem(PUSH_OPTED_OUT_STORAGE_KEY, 'true');
  else localStorage.removeItem(PUSH_OPTED_OUT_STORAGE_KEY);
}

// How long sign-out waits for the backend to drop the token before carrying on without it.
const LOGOUT_UNREGISTER_TIMEOUT_MS = 2000;

// The platform the backend records the token under, or null off-native, where there is no FCM token.
export function pushPlatform(): PushPlatform | null {
  const platform = Capacitor.getPlatform();
  return platform === 'ios' || platform === 'android' ? platform : null;
}

export function storedPushToken(): string | null {
  return localStorage.getItem(PUSH_TOKEN_STORAGE_KEY);
}

/**
 * Stop this device's push notifications going to the user who is signing out. Best effort and bounded:
 * a slow or failing request must not hold up sign-out, and if it never lands, the next person to sign in
 * on the device takes the token over anyway.
 *
 * Must run while the session's credentials are still in place — the request is authenticated as the user
 * whose token it removes.
 */
export async function unregisterPushTokenOnLogout(): Promise<void> {
  const token = storedPushToken();
  const platform = pushPlatform();
  if (!token || !platform || !franchiseApi.isConfigured()) return;

  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<void>(resolve => {
    timer = setTimeout(resolve, LOGOUT_UNREGISTER_TIMEOUT_MS);
  });
  try {
    await Promise.race([franchiseApi.unregisterPushToken(token, platform), timeout]);
  } catch (error) {
    console.warn('[pushTokens] Could not unregister push token on logout:', error);
  } finally {
    clearTimeout(timer);
  }
}
